// Shared formatting helpers for the agent activity-log renderers.

export const formatDate = (iso: string | null): string => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

export const formatMs = (ms: number | null | undefined): string => {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds}s` : `${minutes}m ${seconds}s`;
};

// Walks a parsed JSON value and any nested string that itself parses as JSON
// gets unfurled into its parsed form. Agents frequently emit a top-level JSON
// object whose values contain stringified JSON (e.g. scoring_details), which
// shows up as an unreadable "{\\n  \\\"key\\\": ..." blob when stringified
// naively. After unfurling, JSON.stringify(_, null, 2) produces something a
// human can actually read.
export const unfurlEmbeddedJson = (value: unknown): unknown => {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))
    ) {
      try {
        return unfurlEmbeddedJson(JSON.parse(trimmed));
      } catch {
        return value;
      }
    }
    return value;
  }
  if (Array.isArray(value)) return value.map(unfurlEmbeddedJson);
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = unfurlEmbeddedJson(v);
    }
    return out;
  }
  return value;
};

export const formatToolInput = (input: unknown): string => {
  let parsed: unknown = input;
  if (typeof input === 'string') {
    try {
      parsed = JSON.parse(input);
    } catch {
      return input;
    }
  }
  try {
    return JSON.stringify(unfurlEmbeddedJson(parsed) ?? null, null, 2);
  } catch {
    return String(input ?? '');
  }
};
